import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { FeatureUserStore } from '@h5s/feature-user';
import { AuthService, UtilAuth } from '@h5s/util-auth';
import { LoggerService } from '@h5s/util-logger';
import { Observable, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { buildLogoutInProgressError } from '../errors/models/logout-in-progress-error';

/**
 * Interceptor per aggiungere Property-Access-Token alle richieste HTTP
 * Se l'URL della richiesta è tra quelli permessi, aggiunge il PrAT nell'header
 *
 * @param req La richiesta HTTP
 * @param next Il gestore della richiesta
 * @returns Un Observable con l'evento HTTP
 */
export const pratInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  /** Services */
  const logger = inject(LoggerService).getLogger('PratInterceptor');
  const router = inject(Router);
  const featureUserStore = inject(FeatureUserStore);
  const authService = inject(AuthService);

  const propertyAccessToken = featureUserStore.userAccessProperty().propertyAccessToken;
  const allowedUrls = [environment.apiConfig.restUrl.toLowerCase(), environment.apiConfig.graphqlUrl.toLowerCase()];

  const url = req.url.toLowerCase();
  const isUrlAllowed = allowedUrls.some((allowed) => url.startsWith(allowed));

  /**
   * Controlla se l'URL è consentito e se è in corso il logout
   * dopodicché interrompe la richiesta con un errore specifico per evitare che
   * il flusso observable completi senza emettere nemmeno un valore andando in errore
   */
  if (isUrlAllowed && authService.logoutInProgress()) {
    return throwError(() => buildLogoutInProgressError());
  }

  /**
   * Il PrAT è obbligatorio per ogni chiamata
   * Se per qualsiasi motivo non è più presente l'utente deve impostarlo nuovamente
   */
  if (isUrlAllowed && !propertyAccessToken) {
    logger.error('Property-Access-Token is missing');
    router.navigate(['/pick-your-place']);
  }

  /** Controlla se l'URL è tra quelli permessi e se il PrAT è presente */
  if (isUrlAllowed && propertyAccessToken) {
    req = UtilAuth.addPropertyAccessTokenHeader(req, propertyAccessToken);
  }

  return next(req);
};
